'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { useEvent, useUnmount } from 'react-use';

import type { AccessibilityPluginAdapter } from '@/lib/accessibility';
import { findAccessibilityPlugin, getAccessibilityAdapter } from '@/lib/accessibility';
import { isAccessibilitySyncMessage, POST_MESSAGE_TYPES } from '@/lib/constants/post-message-types';
import { isCrossOriginIframe } from '@/lib/utils/dom';

import { useConfig } from './config-provider';

export function AccessibilityPluginSync() {
  const config = useConfig();
  const plugin = useMemo(
    () => findAccessibilityPlugin(config?.widget?.plugins),
    [config?.widget?.plugins],
  );
  const [adapter, setAdapter] = useState<AccessibilityPluginAdapter | null>(null);

  useEffect(() => {
    if (!plugin) return;
    let mounted = true;

    import('@/styles/accessibility.css');
    getAccessibilityAdapter(plugin).then(a => mounted && a && setAdapter(a));

    return () => {
      mounted = false;
    };
  }, [plugin]);

  const handleMessage = useCallback(
    (event: Event) => {
      const messageEvent = event as MessageEvent;

      if (!adapter) return;
      if (!isAccessibilitySyncMessage(messageEvent.data)) return;
      if (messageEvent.data.plugin !== adapter.pluginId) return;

      adapter.applyState({
        classes: messageEvent.data.classes,
        dataAttrs: messageEvent.data.dataAttrs,
        colors: messageEvent.data.colors,
      });
    },
    [adapter],
  );

  useEvent('message', handleMessage, isCrossOriginIframe() ? window : undefined);

  useEffect(() => {
    if (!adapter || !isCrossOriginIframe()) return;

    window.parent.postMessage(
      { type: POST_MESSAGE_TYPES.ACCESSIBILITY_STATE_REQUEST, plugin: adapter.pluginId },
      '*',
    );
  }, [adapter]);

  useEffect(() => {
    if (!isCrossOriginIframe()) return;

    const handleTextReaderClick = (e: MouseEvent) => {
      if (!document.body.hasAttribute('data-a11y-text-reader')) return;

      const target = e.target as HTMLElement;
      const text = target.textContent?.trim();
      if (!text) return;

      window.parent.postMessage({ type: POST_MESSAGE_TYPES.TEXT_TO_SPEECH_REQUEST, text }, '*');
    };

    document.addEventListener('click', handleTextReaderClick);
    return () => document.removeEventListener('click', handleTextReaderClick);
  }, []);

  useUnmount(() => adapter?.cleanup());

  return null;
}
